import type { ImgHTMLAttributes } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useInView } from 'react-intersection-observer';
import { z } from 'zod';
import { styled } from '@components-shared/src/stitches';

const DownloadedImagePostMessageEvent = z.object({
  data: z.nullable(z.string()),
});

type DownloadedImagePostMessageEvent = (typeof DownloadedImagePostMessageEvent)['_output'];

/*
 * Since web workers don't support SSR, we need to dynamically import the worker
 */
export const DynamicWebWorkerImage = React.memo(function DynamicWebWorkerImage({
  src,
  lazy = true,
  ...props
}: ImgHTMLAttributes<HTMLImageElement> & {
  src: string;
  lazy?: boolean;
}) {
  const { inView, ref } = useInView();
  const [imageObjectUrl, setImageObjectUrl] = useState<string | null>(null);
  const workerRef = useRef<Worker>();

  useEffect(() => {
    const worker = (workerRef.current = new Worker(new URL('./worker', import.meta.url)));

    function closeWorker() {
      try {
        worker.terminate();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    }

    worker.onmessage = (e: DownloadedImagePostMessageEvent) => {
      const parsedEvent = DownloadedImagePostMessageEvent.safeParse(e);

      if (!parsedEvent.success) {
        // eslint-disable-next-line no-console
        console.error(parsedEvent.error);
        setImageObjectUrl(src);
        closeWorker();
        return;
      }

      if (parsedEvent.data.data === null) {
        setImageObjectUrl(src);
        closeWorker();
        return;
      }

      setImageObjectUrl(parsedEvent.data.data);
    };

    worker.onerror = e => {
      // eslint-disable-next-line no-console
      console.error(e?.message || 'An error occurred in the web worker');

      // Fallback to fetching image on main thread
      setImageObjectUrl(src);
      closeWorker();
    };

    return closeWorker;
  }, [src]);

  const fetchImageWithWebWorker = useCallback(async (url: string) => {
    // Convert relative url to absolute url since web worker can't access relative urls
    const absolutePath = new URL(url, document.baseURI).href;

    workerRef.current?.postMessage(absolutePath);
  }, []);

  useEffect(() => {
    if ((inView || !lazy) && src) fetchImageWithWebWorker(src);

    // Clean up the cache
    // Service worker cache is not cleaned up automatically
    const cleanUpCache = async () => {
      const cache = await caches.open('IMAGES_V1');
      const keys = await cache.keys();
      const maxCacheSize = 0.25 * 1024 * 1024 * 1024; // 0.25 GB
      let cacheSize = 0;

      // Convert keys array to a regular array and sort it by Last-Modified timestamp
      const sortedKeys = [...keys].sort((a, b) => {
        return (a.headers.get('last-modified') || '').localeCompare(
          b.headers.get('last-modified') || '',
        );
      });

      for (const request of sortedKeys) {
        const response = await cache.match(request);
        if (response) {
          cacheSize += Number(response.headers.get('content-length')) || 0;
          if (cacheSize > maxCacheSize) {
            // Delete cache entry
            await cache.delete(request);
            cacheSize -= Number(response.headers.get('content-length')) || 0;
          }
        }
      }
    };

    cleanUpCache();
  }, [src, inView, fetchImageWithWebWorker, lazy]);

  const onLoad = useCallback(() => {
    try {
      workerRef.current?.terminate();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  }, []);

  return imageObjectUrl ? (
    <StyledImage ref={lazy ? ref : null} src={imageObjectUrl} onLoad={onLoad} {...props} />
  ) : (
    <div
      ref={lazy ? ref : null}
      {...props}
      className={clsx(props.className, 'loading after:rounded-sm')}
    />
  );
});

const StyledImage = styled('img', {
  position: 'relative', // Avoid background blur from overlaying image
});

// Default export required for dynamic import
export default DynamicWebWorkerImage;
